import { AuthManager } from '@store/AuthManager';
import { getConfig } from '@utils/config';
import { formStrings } from '@utils/forms';
import { tyrannSentryMiddleware } from '@utils/tracing';
import { IS_NODE } from '@utils/types';
import { AxiosRequestConfig, isAxiosError } from 'axios';
import chalk from 'chalk';
import * as t from 'io-ts';
import { tyrann, TyrannMiddleware, TyrannOptions } from 'tyrann-io';
import { TyrannApis } from 'tyrann-io/dist/types';

export const axiosRequestConfig = (): AxiosRequestConfig => ({
  validateStatus: (status: number) => status < 500,
  baseURL: getConfig().serverUrl,
  headers: AuthManager.shared.key
    ? {
        ...getAuthHeaders(),
        ...getBaseHeaders(),
      }
    : getBaseHeaders(),
  withCredentials: true,
});

export const impersonateAxiosRequestConfig = () => ({
  validateStatus: (status: number) => status < 500,
  baseURL: new URL(getConfig().serverUrl).origin,
  headers: AuthManager.shared.key
    ? {
        ...getAuthHeaders(),
        ...getBaseHeaders(),
      }
    : getBaseHeaders(),
  withCredentials: true,
});

export const getAuthHeaders = (): Record<string, string> => {
  if (AuthManager.shared.impersonatedUser) {
    return {
      Authorization: `${AuthManager.shared.auth_type} ${AuthManager.shared.key}`,
      'Impersonate-User-Id': `${AuthManager.shared.impersonatedUser}`,
      'Impersonate-Original-User-Id': `${AuthManager.shared.originalUser}`,
    };
  } else {
    return {
      Authorization: `${AuthManager.shared.auth_type} ${AuthManager.shared.key}`,
    };
  }
};

export const getBaseHeaders = () => {
  return {
    accept: 'application/json',
  };
};

export const openBlob = (blob: Blob, name: string, type?: string, download?: boolean) => {
  const createObjURL = (file: File) => {
    if (window.webkitURL) {
      return window.webkitURL.createObjectURL(file);
    }
    if (window.URL && window.URL.createObjectURL) {
      return window.URL.createObjectURL(file);
    }
    return null;
  };

  const file = new File([blob], name, { type });
  const objectUrl = createObjURL(file);

  if (download) {
    const link = document.createElement('a');
    link.href = objectUrl || '';
    link.download = name;
    link.click();
  } else {
    window.open(objectUrl!);
  }
};

export interface DownloadURLOptions {
  url: string;
  name: string;
  type?: string;
  open?: boolean;
  method?: string;
  body?: {};
  credentials?: RequestCredentials;
  download?: boolean;
}

export const downloadUrl = async (options: DownloadURLOptions) => {
  const {
    url,
    name,
    open = true,
    type,
    method,
    body,
    credentials = 'include',
    download = true,
  } = options;

  const response = url.match(/^https?:\/\/d2d-s3.s3/)
    ? await fetch(url)
    : await fetch(url.match(/^https?:\/\//) ? url : `${getConfig().serverUrl}${url}`, {
        credentials,
        method,
        body: body ? JSON.stringify(body) : undefined,
        headers: {
          ...(credentials !== 'omit' ? getAuthHeaders() : {}),
          ...(body
            ? {
                'content-type': 'application/json',
              }
            : {}),
        },
      });

  if (!response.ok) {
    throw new Error(formStrings.downloadError);
  }

  const blob = await response.blob();
  if (open) {
    openBlob(blob, name, type ?? response.headers.get('Content-Type') ?? undefined, download);
  }
  return blob;
};

export const downloadZip = async (urls: string[], name: string) => {
  const response = await fetch(`${getConfig().serverUrl}/file/download/`, {
    method: 'POST',
    body: JSON.stringify({ urls: urls.map(decodeURIComponent) }),
    // URLs are encoded and sent to client, but the storage cannot find the file if the URL is encoded.
    credentials: 'include',
    headers: {
      ...getAuthHeaders(),
      'content-type': 'application/json',
    },
  });

  if (response.ok) {
    openBlob(await response.blob(), name, 'application/zip');
  } else {
    throw new Error(formStrings.downloadError);
  }
};

export const objectError = t.partial({
  message: t.string,
  object_id: t.number,
});

const warnAdminApiMiddleware: TyrannMiddleware = async (callAction, localOptions, next) => {
  if (callAction.name.startsWith('/dashboard') && !location.pathname.startsWith('/admin')) {
    console.warn(
      'Calling /dashboard API on non-admin pages. This probably leads to a log out when the user not authorised.',
    );
  }

  return await next();
};

const logApiMiddleware: TyrannMiddleware = async (callAction, localOptions, next) => {
  const now = (performance.now() / 1000).toFixed(1);
  const before = performance.now();

  try {
    return await next();
  } finally {
    console.info(
      chalk.bgBlackBright(`[${now}s +${(performance.now() - before).toFixed(0)}ms]`.padEnd(16)),
      chalk.cyan(callAction.method.toUpperCase().padEnd(6)),
      callAction.name,
    );
  }
};

const networkErrorRedirectMiddleware: TyrannMiddleware = async (callAction, localOptions, next) => {
  try {
    return await next();
  } catch (e) {
    if (isAxiosError(e) && !e.response && e.code === 'ERR_NETWORK') {
      if (!window.location.pathname.startsWith('/auth/')) {
        AuthManager.shared.clear();
        window.location.href = '/auth/Login';
        return await new Promise(() => {});
      }
    }
    throw e;
  }
};

const jwtRefreshApiMiddleware: TyrannMiddleware = async (callAction, localOptions, next) => {
  await AuthManager.shared.checkTokenRefresh();
  return await next();
};

export const createDefaultTyrann = <Apis extends TyrannApis>(
  apis: Apis,
  options?: TyrannOptions,
) => {
  return tyrann(apis, {
    ...options,
    axiosRequestConfig,
    middlewares: IS_NODE
      ? [logApiMiddleware, jwtRefreshApiMiddleware]
      : [networkErrorRedirectMiddleware, warnAdminApiMiddleware, tyrannSentryMiddleware, jwtRefreshApiMiddleware],
  });
};
